import React, { useState, useEffect, useRef } from 'react';
import { apiPut, apiGet } from '../../utils/apiFetch';
import apiPath from '../../utils/pathObj';
import { useForm } from 'react-hook-form';
import useToastContext from 'hooks/useToastContext';
import OInputField from 'components/reusable/OInputField';
import { useTranslation } from 'react-i18next';
import { preventMaxInput } from 'utils/validations';
import formValidation from '../../utils/formValidation';
import ErrorMessage from 'components/ErrorMessage';
import OImage from 'components/reusable/OImage';
import helpers from 'utils/helpers';
import { validationRules } from '../../utils/constants';

const AccountantOnboardingEdit = ({
  allOnboarding,
  handleEdit,
  currentItem
}) => {
  const { t } = useTranslation();
  const profileRef = useRef();
  const notification = useToastContext();
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      name: currentItem?.name || '',
      email: currentItem?.email || '',
      mobile: currentItem?.mobile || '',
      address: currentItem?.address || '',
      city: currentItem?.city || '',
      countryCode: currentItem?.countryCode || '',
    }
  });

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(currentItem?.Country?._id || '');
  const [selectedState, setSelectedState] = useState(currentItem?.State?._id || '');
  const [passportPic, setPassportPic] = useState(currentItem?.profilePicture || null);
  const [fileMsg, setFileMsg] = useState({ profilePicture: '' });

  // Set initial values when component mounts
  useEffect(() => {
    if (currentItem) {
      setValue('name', currentItem.name);
      setValue('email', currentItem.email);
      setValue('mobile', currentItem.mobile);
      setValue('address', currentItem.address);
      setValue('city', currentItem.city);
      setValue('countryCode', currentItem.countryCode);
      setSelectedCountry(currentItem.Country?._id);
      setSelectedState(currentItem.State?._id);
      setPassportPic(currentItem.profilePicture);
    }
  }, [currentItem, setValue]);

  const validateFiles = (file) => {
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!file) return true;
    return supportedTypes.includes(file.type);
  };

  const handleFileChangeLogo = (e) => {
    const file = e?.target?.files[0];
    if (!file) return;

    const isValidImage = validateFiles(file);
    const fileSize = (file.size / (1024 * 1024)).toFixed(2);

    if (fileSize > 3) {
      setFileMsg({ profilePicture: 'Please select image below 3Mb.' });
      profileRef.current.value = '';
      return;
    }

    if (!isValidImage) {
      setFileMsg({ profilePicture: 'Only jpeg, jpg and png are supported.' });
      profileRef.current.value = '';
      return;
    }

    setFileMsg({ profilePicture: '' });
    setPassportPic(file);
  };

  const getCountryList = async () => {
    try {
      const res = await apiGet(apiPath.getCountryList, {});
      setCountries(res?.data?.results?.allCountries || []);
    } catch (error) {
      console.log('error:', error);
    }
  };

  const getStateList = async (countryId) => {
    try {
      setLoadingStates(true);
      const res = await apiGet(`${apiPath.getStateList}/${countryId}`, {});
      setStates(res?.data?.results?.allStates || []);
    } catch (error) {
      console.error('Error loading states:', error);
    } finally {
      setLoadingStates(false);
    }
  };

  useEffect(() => {
    getCountryList();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      getStateList(selectedCountry);
    } else {
      setStates([]);
      setSelectedState('');
    }
  }, [selectedCountry]);

  const checkFields = () => {
    const hasCountry = Boolean(selectedCountry);
    const hasState = Boolean(selectedState);
    const hasCity = Boolean(watch('city'));
    const hasProfilePicture = Boolean(passportPic);

    return hasCountry && hasState && hasCity && !(hasProfilePicture && fileSizeExceedsLimit());
  };

  const fileSizeExceedsLimit = () => {
    if (!passportPic || !passportPic.size) return false;
    const fileSize = (passportPic.size / (1024 * 1024)).toFixed(2);
    return fileSize > 3;
  };

  const onSubmit = async (data) => {
    try {
      if (!checkFields()) {
        if (!selectedCountry) setError('country', { type: 'manual', message: 'Please select country' });
        if (!selectedState) setError('state', { type: 'manual', message: 'Please select state' });
        if (!data.city) setError('city', { type: 'manual', message: 'Please enter city name' });
        return;
      }

      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('mobile', data.mobile);
      formData.append('address', data.address);
      formData.append('city', data.city);
      formData.append('countryID', selectedCountry);
      formData.append('stateID', selectedState);
      if (passportPic) {
        if (typeof passportPic === 'string') {
          // If it's a string (existing image path), no need to append as file
          formData.append('profilePicture', passportPic);
        } else {
          // If it's a File object (new image), append it
          formData.append('profilePicture', passportPic);
        }
      }

      const res = await apiPut(
        apiPath.accountantOnboardingEdit + '/' + currentItem._id,
        formData
      );

      if (res?.data?.success) {
        allOnboarding();
        handleEdit(false);
        notification.success(res?.data?.message);
      } else {
        notification.error(res?.data?.message);
      }
    } catch (err) {
      console.log('err:', err);
      notification.error('An error occurred while updating accountant');
    }
  };

  return (
    <>
      <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
        <div className='relative w-auto my-6 mx-auto max-w-3xl'>
          <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
            <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[762px]'>
              <div className='flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t'>
                <h3 className='text-xl font-semibold'>
                  {t('EDIT_ACCOUNTANT_ONBOARDING')}
                </h3>
                <button
                  className='ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none'
                  onClick={() => handleEdit(false)}
                  title='Close'
                >
                  <span className='text-[#B8BBBF] text-4xl' title='Close'>
                    ×
                  </span>
                </button>
              </div>
              <div className='relative p-6 flex-auto'>
                <div className='grid sm:grid-cols-2'>
                  {/* Name Field */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                    <OInputField
                      wrapperClassName='relative z-0 w-full group'
                      name='name'
                      inputLabel={
                        <>
                          {t('ACCOUNTANT_NAME')}
                          <span className='text-red-500'>*</span>
                        </>
                      }
                      type='text'
                      autoFocus
                      maxLength={50}
                      onInput={e => preventMaxInput(e, 50)}
                      register={register('name', formValidation['accountantName'])}
                      errors={errors}
                    />
                  </div>

                  {/* Email Field */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                    <OInputField
                      wrapperClassName='relative z-0 w-full group'
                      name='email'
                      inputLabel={
                        <>
                          {t('OFFICIAL_EMAIL_ADDRESS')}
                          <span className='text-red-500'>*</span>
                        </>
                      }
                      type='text'
                      maxLength={50}
                      onInput={e => preventMaxInput(e, 50)}
                      register={register('email', formValidation['email'])}
                      errors={errors}
                    />
                  </div>

                  {/* Country Field */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                    <div className='relative z-0 w-full group'>
                      <select
                        type='select'
                        id='country'
                        name='country'
                        className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
                        onChange={e => {
                          setSelectedCountry(e.target.value);
                          clearErrors('country');
                        }}
                        placeholder=' '
                        value={selectedCountry}
                      >
                        <option value=''>
                          {t('SELECT_COUNTRY_NAME')}{' '}
                          <span className='text-red-500'>*</span>
                        </option>
                        {countries?.length &&
                          countries?.map((i, key) => (
                            <option key={key} value={i._id}>
                              {i.name}
                            </option>
                          ))}
                      </select>
                      <label
                        htmlFor='country'
                        className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                      >
                        {t('COUNTRY')}
                        <span className='text-red-500'>*</span>
                      </label>
                      {errors.country && <ErrorMessage message={errors.country.message} />}
                    </div>
                  </div>

                  {/* Mobile Number with Country Code */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7 flex'>
                    {countries?.length > 0 &&
                      countries?.map(index => {
                        return (
                          selectedCountry === index?._id && (
                            <div
                              key={index?._id}
                              className='w-[50px] h-[56px] mr-3 block py-4 px-3 text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
                            >
                              +{index?.countryCode}
                            </div>
                          )
                        );
                      })}

                    <div className='relative z-0 w-full group'>
                      <OInputField
                        wrapperClassName='relative z-0 w-full group'
                        type='number'
                        name='mobile'
                        id='mobile'
                        inputLabel={
                          <>
                            {t('MOBILE_NUMBER')}
                            <span className='text-red-500'>*</span>
                          </>
                        }
                        min={0}
                        onKeyDown={e => {
                          if (['-', '+', 'e'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onInput={e => preventMaxInput(e, 15)}
                        register={register('mobile', formValidation['mobile'])}
                        errors={errors}
                      />
                    </div>
                  </div>

                  {/* State Field */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                    <div className='relative z-0 w-full group'>
                      <select
                        type='select'
                        name='state'
                        id='state'
                        className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
                        placeholder=' '
                        value={selectedState}
                        onChange={e => {
                          setSelectedState(e.target.value);
                          clearErrors('state');
                        }}
                        disabled={!selectedCountry || loadingStates}
                      >
                        <option value=''>
                          {t('SELECT_STATE')}
                          <span className='text-red-500'>*</span>
                        </option>
                        {loadingStates ? (
                          <option value='' disabled>Loading states...</option>
                        ) : (
                          states?.length &&
                          states?.map((i, key) => (
                            <option key={key} value={i._id}>
                              {i.name}
                            </option>
                          ))
                        )}
                      </select>
                      <label
                        htmlFor='state'
                        className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                      >
                        {t('STATE')}
                        <span className='text-red-500'>*</span>
                      </label>
                      {errors.state && <ErrorMessage message={errors.state.message} />}
                    </div>
                  </div>

                  {/* City Field */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                    <div className='relative z-0 w-full group'>
                      <OInputField
                        wrapperClassName='relative z-0 w-full group'
                        name='city'
                        inputLabel={
                          <>
                            {t('CITY')}
                            <span className='text-red-500'>*</span>
                          </>
                        }
                        type='text'
                        register={register('city', {
                          required: 'Please enter city name',
                          minLength: {
                            value: 2,
                            message: 'City name must be at least 2 characters'
                          },
                          maxLength: {
                            value: 100,
                            message: 'City name cannot exceed 100 characters'
                          },
                          onChange: (e) => clearErrors('city')
                        })}
                        errors={errors}
                        disabled={!selectedState}
                      />
                    </div>
                  </div>

                  {/* Address Field */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                    <div className='relative z-0 w-full group'>
                      <OInputField
                        wrapperClassName='relative z-0 w-full group'
                        name='address'
                        inputLabel={
                          <>
                            {t('ADDRESS')}
                            <span className='text-red-500'>*</span>
                          </>
                        }
                        type='text'
                        maxLength={300}
                        onInput={e => preventMaxInput(e, 300)}
                        register={register('address', formValidation['addresses'])}
                        errors={errors}
                      />
                    </div>
                  </div>

                  {/* Profile Picture Field */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                    <div className='relative z-0 w-full group'>
                      <div className='block py-3 h-14 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'>
                        <input
                          type='file'
                          name='profilePicture'
                          className='form-control'
                          ref={profileRef}
                          accept='image/png,image/jpeg,image/jpg'
                          onChange={handleFileChangeLogo}
                        />
                      </div>
                      <label
                        htmlFor='floating_file'
                        className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-1 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                      >
                        {t('IMAGE')} (Only image, upto 3Mb)
                      </label>
                      <ErrorMessage
                        message={
                          errors?.profilePicture?.message ||
                          fileMsg.profilePicture
                        }
                      />
                      <div className='flex items-center bg-white'>
                        {passportPic && (
                          <OImage
                            src={
                              typeof passportPic === 'string'
                                ? passportPic
                                : URL.createObjectURL(passportPic)
                            }
                            className='w-24 h-24 mt-5'
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b'>
                <button
                  className='text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150'
                  type='button'
                  onClick={() => handleEdit(false)}
                >
                  {t('O_BACK')}
                </button>
                <button
                  className='bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150'
                  type='submit'
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('O_SAVE')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='opacity-25 fixed inset-0 z-40 bg-black' />
    </>
  );
};

export default AccountantOnboardingEdit;